import React from 'react';

// AI Office panel for the model scorecard: quick PASS / REVIEW / FAIL signals with the
// reasons — Step 0 compliance pre-screen (Gate A origin, Gate B platform), Legal and TPRM
// pre-screens from public information. Decisions are made by the approval gates outside this app.
// Data comes from item.ai_office, built by backend/ai_office.py.
// Written with React.createElement (no JSX) so it can be render-tested without a build.
const h = React.createElement;

const SIG = {
  PASS: { icon: '✅', t: 'PASS', bg: '#d4edda', fg: '#155724', bd: '#a3cfbb' },
  REVIEW: { icon: '⚠️', t: 'REVIEW', bg: '#fff3cd', fg: '#856404', bd: '#ffe69c' },
  FAIL: { icon: '❌', t: 'FAIL', bg: '#f8d7da', fg: '#721c24', bd: '#f1aeb5' },
  'N/A': { icon: '–', t: 'N/A', bg: '#f5f5f5', fg: '#777', bd: '#ddd' },
};
const sig = (s) => SIG[s] || SIG.REVIEW;

function Pill({ status, small }) {
  const s = sig(status);
  return h('span', { style: { fontSize: small ? '9px' : '11px', fontWeight: 'bold', padding: small ? '1px 6px' : '2px 8px', borderRadius: '10px',
    background: s.bg, color: s.fg, border: `1px solid ${s.bd}`, whiteSpace: 'nowrap' } }, `${s.icon} ${s.t}`);
}

function Signal({ title, sub, data, open, onToggle }) {
  const s = sig(data.status);
  return h('div', { style: { flex: '1 1 0', minWidth: '150px', border: `1px solid ${s.bd}`, background: s.bg, borderRadius: '6px', padding: '7px 9px', cursor: 'pointer' },
    onClick: onToggle, title: 'Click for the reasons' },
    h('div', { style: { fontSize: '10px', color: '#555', textTransform: 'uppercase', letterSpacing: '0.4px' } }, title),
    h('div', { style: { fontSize: '15px', fontWeight: 'bold', color: s.fg } }, `${s.icon} ${s.t}`),
    h('div', { style: { fontSize: '10px', color: s.fg, marginTop: '2px' } }, sub),
    h('div', { style: { fontSize: '10px', color: '#2b6cb0', marginTop: '3px' } }, open ? '▲ hide why' : '▼ why'));
}

function Checks({ checks, note }) {
  return h('div', { style: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '6px 8px', marginTop: '6px' } },
    (checks || []).map(c => h('div', { key: c.key, style: { display: 'flex', gap: '6px', alignItems: 'flex-start', fontSize: '11px', padding: '3px 0', borderBottom: '1px dotted #eee' } },
      h(Pill, { status: c.status, small: true }),
      h('div', null, h('strong', null, c.label), `: ${c.why}`,
        c.source ? h('span', { style: { color: '#999' } }, ` (source: ${c.source})`) : null))),
    note ? h('div', { style: { fontSize: '10px', color: '#888', marginTop: '4px' } }, note) : null);
}

function Step0Card({ s0 }) {
  const a = s0.gate_a || {};
  const b = s0.gate_b || {};
  const gate = (title, g, body) => {
    const s = sig(g.status);
    return h('div', { style: { flex: '1 1 0', minWidth: '200px', background: s.bg, border: `1px solid ${s.bd}`, borderRadius: '6px', padding: '8px' } },
      h('div', { style: { background: g.status === 'PASS' ? '#2e7d32' : g.status === 'FAIL' ? '#c62828' : '#b7791f', color: '#fff', fontWeight: 'bold', fontSize: '11px', textAlign: 'center', padding: '4px', borderRadius: '4px', marginBottom: '6px' } }, title),
      body);
  };
  return h('div', { style: { marginTop: '6px' } },
    h('div', { style: { fontSize: '10px', color: '#666', marginBottom: '4px' } }, 'Per EchoStar AI Office policy — models must pass compliance BEFORE technical evaluation'),
    h('div', { style: { display: 'flex', gap: '8px', flexWrap: 'wrap' } },
      gate('GATE A: COUNTRY OF ORIGIN', a, h('div', { style: { fontSize: '11px', color: '#333' } },
        h('div', null, h('strong', null, `${sig(a.status).icon} ${a.label}`), ` — ${a.origin || ''}`),
        h('div', { style: { marginTop: '2px' } }, a.why),
        h('div', { style: { color: '#777', fontSize: '10px', marginTop: '4px' } }, 'US-origin: proceed · Non-US: PROHIBITED · China / Russia: BANNED'))),
      gate('GATE B: PLATFORM / HOSTING', b, h('div', { style: { fontSize: '11px', color: '#333' } },
        h('div', null, h('strong', null, `${sig(b.status).icon} ${b.status}`), ` — ${b.why}`),
        (b.notes || []).map((n, i) => h('div', { key: i, style: { color: '#666', fontSize: '10px', marginTop: '2px' } }, `• ${n}`)),
        h('div', { style: { color: '#777', fontSize: '10px', marginTop: '4px' } }, 'Approved: AWS Bedrock · Databricks · Self-hosted (US open-weight only). Prohibited: Claude.ai / ChatGPT.com / vendor sites without an enterprise agreement')))));
}

// Decision summary: every signal + the measured scores read as ONE recommendation,
// with the step that decided it and what to do next (backend ai_office.decision).
const VERDICT = {
  APPROVED: { bg: '#d1f2eb', fg: '#0b5345', bd: '#9fd8c8' },
  STOP: { bg: '#f8d7da', fg: '#721c24', bd: '#f1aeb5' },
  NOT_READY: { bg: '#fff3cd', fg: '#856404', bd: '#ffe69c' },
  BELOW_BAR: { bg: '#fde8d8', fg: '#8a3b12', bd: '#f5c09a' },
  READY_WITH_REVIEW: { bg: '#e7f0ff', fg: '#084298', bd: '#b6d4fe' },
  READY: { bg: '#d4edda', fg: '#155724', bd: '#a3cfbb' },
};

const HOW_TO_READ = [
  ['AI Office approved', 'In the EchoStar Approved catalog. For an open-weight model the approval also allows pulling its weights and running them on our own AWS GPUs (that exact version only; never overrides the China/Russia rule).'],
  ['LIVE golden suite', 'Our own test prompts, run on the real model and graded by judges. The only score that counts toward approval.'],
  ['Final blended score', 'Live suite × weight + public benchmark × weight (Admin › Scoring, default 60 / 40), compared with the approval threshold (default 80).'],
  ['Public benchmarks', 'Artificial Analysis index / BenchLM: published by third parties. Good for ranking candidates against each other; never enough on their own to approve.'],
  ['SIMULATED', 'Estimates and placeholders. Never used for ranking or approval.'],
  ['Order of the steps', 'Origin → channel → evidence → score → Legal / TPRM / Security. A model that fails an earlier step is not judged on later ones.'],
];

// Reasoning for the AI Office: plain-language paragraphs (backend ai_office.rationale)
// that a reviewer can read top to bottom, or copy into an AI Office packet.
function Reasoning({ dec }) {
  const [copied, setCopied] = React.useState(false);
  const paras = dec.rationale || [];
  if (!paras.length) return null;
  const copy = () => {
    const txt = `${dec.icon} ${dec.label}\n\n${dec.rationale_text || paras.map(p => `${p.title}\n${p.text}`).join('\n\n')}`;
    try { navigator.clipboard.writeText(txt); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (e) { /* no clipboard */ }
  };
  return h('div', { style: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '6px 8px', marginTop: '6px' } },
    h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' } },
      h('strong', { style: { fontSize: '11px', color: '#0f2027' } }, '🧭 Reasoning for the AI Office'),
      h('button', { onClick: copy, title: 'Copy this reasoning as text (for an AI Office packet or email)',
        style: { fontSize: '10px', border: '1px solid #0073bb', color: '#0073bb', background: '#fff', borderRadius: '3px', cursor: 'pointer' } },
        copied ? '✓ Copied' : '📋 Copy')),
    paras.map((p, i) => h('div', { key: i, style: { fontSize: '11px', color: '#333', padding: '3px 0', borderBottom: i < paras.length - 1 ? '1px dotted #eee' : 'none' } },
      h('strong', null, `${p.title}: `), p.text)));
}

function DecisionCard({ dec }) {
  const [open, setOpen] = React.useState(false);
  const [how, setHow] = React.useState(false);
  const [why, setWhy] = React.useState(true);
  const v = VERDICT[dec.verdict] || VERDICT.NOT_READY;
  return h('div', { style: { background: v.bg, border: `1px solid ${v.bd}`, borderRadius: '6px', padding: '8px 10px', marginBottom: '8px' } },
    h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' } },
      h('div', null,
        h('div', { style: { fontSize: '10px', color: '#555', textTransform: 'uppercase', letterSpacing: '0.4px' } }, 'Recommendation for the AI Office'),
        h('div', { style: { fontSize: '15px', fontWeight: 'bold', color: v.fg } }, `${dec.icon} ${dec.label}`),
        h('div', { style: { fontSize: '11px', color: v.fg } }, dec.meaning)),
      h('div', { style: { display: 'flex', gap: '8px' } },
        h('a', { onClick: () => setWhy(!why), style: { fontSize: '10px', color: '#2b6cb0', cursor: 'pointer' } }, why ? '▲ hide reasoning' : '▼ reasoning'),
        h('a', { onClick: () => setOpen(!open), style: { fontSize: '10px', color: '#2b6cb0', cursor: 'pointer' } }, open ? '▲ hide steps' : '▼ how it was decided'),
        h('a', { onClick: () => setHow(!how), style: { fontSize: '10px', color: '#2b6cb0', cursor: 'pointer' } }, how ? '▲ hide guide' : 'ⓘ how to read the scores'))),
    (dec.next_actions || []).length ? h('div', { style: { marginTop: '5px', fontSize: '11px', color: '#333' } },
      h('strong', null, 'Next: '), dec.next_actions.map((a, i) => h('div', { key: i, style: { marginLeft: '8px' } }, `→ ${a}`))) : null,
    why ? h(Reasoning, { dec }) : null,
    open ? h('div', { style: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '6px 8px', marginTop: '6px' } },
      (dec.steps || []).map(s => h('div', { key: s.key, style: { display: 'flex', gap: '6px', alignItems: 'flex-start', fontSize: '11px', padding: '3px 0', borderBottom: '1px dotted #eee' } },
        h(Pill, { status: s.status, small: true }),
        h('div', null, h('strong', null, s.label), `: ${s.why || ''}`,
          s.action ? h('div', { style: { color: '#2b6cb0' } }, `→ ${s.action}`) : null))),
      h('div', { style: { fontSize: '10px', color: '#888', marginTop: '4px' } }, dec.note)) : null,
    how ? h('div', { style: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '6px 8px', marginTop: '6px', fontSize: '11px' } },
      HOW_TO_READ.map(([k, t]) => h('div', { key: k, style: { padding: '2px 0' } }, h('strong', null, `${k}: `), t))) : null);
}

export default function AiOfficePanel({ item, getAuthHeaders, onUpdated }) {
  const ao = item && item.ai_office;
  const [open, setOpen] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  if (!ao || ao.error) return h('div', { style: { fontSize: '11px', color: '#888' } }, ao && ao.error ? `AI Office signals unavailable: ${ao.error}` : 'Refresh the catalog to load the AI Office signals.');
  const post = async (url, body) => {
    setBusy(true); setErr('');
    try {
      const r = await fetch(url, { method: 'POST', headers: getAuthHeaders ? getAuthHeaders() : {}, body: JSON.stringify(body) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      if (onUpdated && d.item) onUpdated(d.item);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };
  const s0 = ao.step0 || {}, lg = ao.legal || {}, tp = ao.tprm || {};
  const t = (k) => setOpen(open === k ? null : k);
  return h('div', { style: { border: '1px solid #cbd5e0', borderRadius: '6px', padding: '8px', marginBottom: '10px', background: '#fbfcfe' } },
    h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' } },
      h('strong', { style: { fontSize: '12px', color: '#0f2027' } }, '🏛️ AI Office signals'),
      h('button', { disabled: busy, onClick: () => post('/api/ai-office-prescreen', { model_id: item.model_id }), title: 'Re-check open-weight availability and the Hugging Face licence',
        style: { fontSize: '10px', border: '1px solid #0073bb', color: '#0073bb', background: '#fff', borderRadius: '3px', cursor: busy ? 'wait' : 'pointer' } }, busy ? 'Refreshing…' : '↻ Refresh public facts')),
    ao.decision ? h(DecisionCard, { dec: ao.decision }) : null,
    h('div', { style: { display: 'flex', gap: '6px', flexWrap: 'wrap' } },
      h(Signal, { title: 'Compliance pre-screen (Step 0)', sub: `Origin ${s0.gate_a && s0.gate_a.status} · Platform ${s0.gate_b && s0.gate_b.status}`, data: s0, open: open === 's0', onToggle: () => t('s0') }),
      h(Signal, { title: 'Legal pre-screen', sub: 'licence · terms · origin (public info)', data: lg, open: open === 'legal', onToggle: () => t('legal') }),
      h(Signal, { title: 'TPRM pre-screen', sub: 'vendor · channel · data · residency', data: tp, open: open === 'tprm', onToggle: () => t('tprm') })),
    open === 's0' ? h('div', null, h('div', { style: { fontSize: '11px', marginTop: '6px', color: sig(s0.status).fg } }, s0.message), h(Step0Card, { s0 })) : null,
    open === 'legal' ? h(Checks, { checks: lg.checks, note: lg.note }) : null,
    open === 'tprm' ? h(Checks, { checks: tp.checks, note: tp.note }) : null,
    h('div', { style: { fontSize: '10px', color: '#888', marginTop: '6px' } }, 'Information for the AI Office approval gates (SteerCo, TPRM, Legal, EAC). Rules: Model Evaluation › Pre-screen Rules.'),
    err ? h('div', { style: { color: '#721c24', fontSize: '11px', marginTop: '4px' } }, err) : null);
}

// Compact chip for the catalog table.
export function Step0Chip({ item }) {
  const s0 = item && item.ai_office && item.ai_office.step0;
  if (!s0) return null;
  const s = sig(s0.status);
  return h('span', { title: `${s0.message} Origin: ${(s0.gate_a || {}).why || ''} · Platform: ${(s0.gate_b || {}).why || ''}`,
    style: { marginLeft: '4px', fontSize: '9px', fontWeight: 'bold', color: s.fg, background: s.bg, border: `1px solid ${s.bd}`, borderRadius: '8px', padding: '1px 6px', whiteSpace: 'nowrap' } },
    `Step 0 ${s.icon}`);
}
